import { useEffect, useMemo, useState } from "react";

import { Box, Dialog, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

import { User } from "src/api/client-api/user/useAllUsers";
import { useRoles } from "src/api/client-api/roles/useRoles";
import { OrganizationRoleCompany, OrganizationRolesSection } from "src/pages/Settings/Users/OrganizationRolesSection";
import {
    canManagePlatformAdminTarget,
    deriveOrganizationRoleOptions,
    isPlatformAdmin,
} from "src/pages/Settings/Users/userModalRoles";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

type UserOrganizationRolesModalProps = {
    open: boolean;
    onClose: () => void;
    user: User | null;
};

export const UserOrganizationRolesModal = (props: UserOrganizationRolesModalProps) => {
    const { open, onClose, user } = props;
    const { t } = useTranslation();
    const { isAdmin } = useFoodpilotContext();
    const { roles, rolesLoading, getRoleDisplayName } = useRoles();
    const [companies, setCompanies] = useState<OrganizationRoleCompany[]>([]);
    const canManageRoles = canManagePlatformAdminTarget(isAdmin(), user?.roles);

    const organizationRoleOptions = useMemo(
        () => deriveOrganizationRoleOptions(roles, getRoleDisplayName),
        [roles, getRoleDisplayName],
    );

    useEffect(() => {
        if (open) {
            setCompanies(user?.organizations ?? []);
        }
    }, [open, user]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: (theme) => `1px solid ${theme.custom.grey[500]}`,
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ marginBottom: "8px" }}>
                        {t("users.organizationRoles", "Companies & Roles")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ color: (theme) => theme.custom.grey[2500] }}>
                        {user?.email}
                    </Typography>
                </Box>
                <Box
                    component="button"
                    type="button"
                    aria-label={t("common.close", "Close")}
                    onClick={onClose}
                    sx={{
                        display: "flex",
                        padding: "8px",
                        border: 0,
                        borderRadius: "50%",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: (theme) => theme.custom.grey[300] },
                    }}
                >
                    <CloseIcon />
                </Box>
            </Box>
            <Box sx={{ padding: "32px" }}>
                <OrganizationRolesSection
                    userId={user?.id}
                    companies={companies}
                    roleOptions={organizationRoleOptions}
                    isRolesLoading={rolesLoading}
                    canManageRoles={canManageRoles}
                    isTargetPlatformAdmin={isPlatformAdmin(user?.roles)}
                    onCompaniesChange={setCompanies}
                />
            </Box>
        </Dialog>
    );
};
